import { PaletteMode } from '@mui/material';
import { lighten } from '@mui/material/styles';
import { initI18next } from 'i18n';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isInIframe, resolveParentOrigin } from 'utils/iframe';

import { StorageKey } from '@shared/hooks/usePersistedState';
import { getContrastText } from '@shared/theme/mui/palettes/utils';
import { availableDateFormats, availableTimeFormats } from '@shared/utils/dateFormat';

import { PostMessageContext, SDK } from './PostMessageContext';
import { SDKPaletteModeContext } from './SDKPaletteModeContext';
import { SdkDialogPropsProvider } from './SdkDialogPropsProvider';
import { ViewportDimensions, ViewportDimensionsContext } from './ViewportDimensionsContext';

interface Props {
  readonly children: ReactNode;
}

export function PostMessageProvider({ children }: Props) {
  const [sdk, setSdk] = useState<SDK | null>(null);
  const [sdkPaletteMode, setSdkPaletteMode] = useState<PaletteMode | undefined>(undefined);
  const [viewportDimensions, setViewportDimensions] = useState<ViewportDimensions | null>(null);
  const readyCalled = useRef(false);
  const inIFrame = useMemo(() => isInIframe(), []);
  const parentOrigin = useMemo(() => (inIFrame ? resolveParentOrigin() : null), [inIFrame]);

  useEffect(() => {
    if (!inIFrame) {
      initI18next();
    }
  }, [inIFrame]);

  const setSdkAndStyles = useCallback((newSdk: SDK) => {
    initI18next(newSdk.language);
    setSdk(newSdk);

    if (
      newSdk.dateFormat !== undefined &&
      (availableDateFormats as readonly string[]).includes(newSdk.dateFormat)
    ) {
      localStorage.setItem(StorageKey.DateFormat, JSON.stringify(newSdk.dateFormat));
    }
    if (
      newSdk.timeFormat !== undefined &&
      (availableTimeFormats as readonly string[]).includes(newSdk.timeFormat)
    ) {
      localStorage.setItem(StorageKey.TimeFormat, JSON.stringify(newSdk.timeFormat));
    }

    if (newSdk.colors !== undefined) {
      const root = document.documentElement;
      const setVar = (name: string, value: string) => root.style.setProperty(name, value);

      if (newSdk.colors.iconPrimary) {
        setVar('--icon-primary', newSdk.colors.iconPrimary);
        setVar('--icon-primary-60', lighten(newSdk.colors.iconPrimary, 0.4));
        setVar('--icon-primary-30', lighten(newSdk.colors.iconPrimary, 0.7));
      }
      if (newSdk.colors.iconSecondary) {
        setVar('--icon-secondary', newSdk.colors.iconSecondary);
        setVar('--icon-secondary-60', lighten(newSdk.colors.iconSecondary, 0.4));
      }
      if (newSdk.colors.primary) {
        setVar('--sdk-primary', newSdk.colors.primary);
        setVar('--sdk-primary-contrast', getContrastText(newSdk.colors.primary));
      }
      if (newSdk.colors.bodyBackground) {
        setVar('--body-background', newSdk.colors.bodyBackground);
      }
      if (newSdk.colors.lightThemeBackground) {
        setVar('--sdk-light-bg', newSdk.colors.lightThemeBackground);
      }
      if (newSdk.colors.darkThemeBackground) {
        setVar('--sdk-dark-bg', newSdk.colors.darkThemeBackground);
      }
      if (newSdk.colors.modalBackground) {
        setVar('--modal-background', newSdk.colors.modalBackground);
      }
      if (newSdk.colors.border) {
        setVar('--sdk-border', newSdk.colors.border);
      }
    }
  }, []);

  useEffect(() => {
    if (!inIFrame) {
      return;
    }

    if (parentOrigin === null) {
      console.error('could not resolve the parent origin, postMessage bridge disabled');
      return;
    }

    const eventListener = (event: MessageEvent) => {
      if (!(event.origin === parentOrigin && event.source === window.parent && event.isTrusted)) {
        return;
      }

      let eventData: unknown;
      try {
        eventData = JSON.parse(event.data);
      } catch (error) {
        console.error('error parsing postMessage event data: ', error);
        return;
      }

      if (typeof eventData !== 'object' || eventData === null) {
        return;
      }

      const message = eventData as {
        type?: unknown;
        data?: Record<string, unknown>;
      };

      switch (message.type) {
        case 'sendSdk':
          if (typeof message.data?.sdk === 'object' && message.data?.sdk !== null) {
            setSdkAndStyles(message.data.sdk as SDK);
          }
          break;
        case 'changeTheme':
          if (typeof message.data?.theme === 'string') {
            setSdkPaletteMode(message.data.theme as PaletteMode);
          }
          break;
        case 'viewportDimensions':
          if (
            typeof message.data?.width === 'number' &&
            typeof message.data?.height === 'number' &&
            typeof message.data?.iframeX === 'number' &&
            typeof message.data?.iframeY === 'number' &&
            typeof message.data?.iframeWidth === 'number' &&
            typeof message.data?.iframeHeight === 'number'
          ) {
            setViewportDimensions({
              width: message.data.width,
              height: message.data.height,
              iframeX: message.data.iframeX,
              iframeY: message.data.iframeY,
              iframeWidth: message.data.iframeWidth,
              iframeHeight: message.data.iframeHeight,
            });
          }
          break;
        default:
          break;
      }
    };

    const root = window.document.getElementById('root');

    const resizeListener = () => {
      const h = root?.offsetHeight;

      if (h) {
        window.parent.postMessage(
          JSON.stringify({
            type: 'resize',
            data: {
              height: h,
            },
          }),
          parentOrigin
        );
      }
    };

    let resizeObserver: ResizeObserver | undefined;

    window.addEventListener('message', eventListener);
    window.document.body.style.overflowY = 'hidden';
    // send ready event
    if (!readyCalled.current) {
      window.parent.postMessage(JSON.stringify({ type: 'ready' }), parentOrigin);
      readyCalled.current = true;
    }

    const interval = setInterval(() => {
      const h = root?.offsetHeight;

      if (h !== 0) {
        clearInterval(interval);
        resizeListener();
      }
    }, 200);

    // create resize observer that is going to react to changes to the root element
    try {
      resizeObserver = new ResizeObserver(entries => {
        let height: number;
        if (entries[0].contentBoxSize && entries[0].contentBoxSize[0]) {
          height = entries[0].contentBoxSize[0]?.blockSize;
        } else {
          height = entries[0].contentRect.height;
        }

        window.parent.postMessage(
          JSON.stringify({
            type: 'resize',
            data: {
              height,
            },
          }),
          parentOrigin
        );
      });

      if (root) resizeObserver.observe(root);
    } catch (error) {
      console.error('error initializing resize observer: ', error);
    }

    return () => {
      clearInterval(interval);
      window.removeEventListener('message', eventListener);
      if (resizeObserver) {
        if (root) {
          resizeObserver.unobserve(root);
        }
        resizeObserver.disconnect();
      }
    };
  }, [inIFrame, parentOrigin, setSdkAndStyles]);

  const value = useMemo(
    () => ({
      _sdk: sdk,
      getSdk: () => sdk,
      setSdk: (sdk: SDK) => setSdk(sdk),
      sendMessage: (value: string) => {
        if (parentOrigin !== null) {
          window.parent.postMessage(value, parentOrigin);
        }
      },
    }),
    [parentOrigin, sdk]
  );

  const viewportDimensionsValue = useMemo(
    () => ({
      viewportDimensions,
      setViewportDimensions,
    }),
    [viewportDimensions]
  );

  if (!inIFrame) {
    return children;
  }

  return (
    <PostMessageContext.Provider value={value}>
      <SDKPaletteModeContext.Provider value={sdkPaletteMode}>
        <ViewportDimensionsContext.Provider value={viewportDimensionsValue}>
          <SdkDialogPropsProvider>{sdk ? children : null}</SdkDialogPropsProvider>
        </ViewportDimensionsContext.Provider>
      </SDKPaletteModeContext.Provider>
    </PostMessageContext.Provider>
  );
}
